import { createAction } from 'redux-actions'
import { handleAPIError } from 'features/app/store/actions'
import { types } from 'features/todolist/store/types'
import moment from 'moment'
import get from 'lodash/get'
import { constants, translations, utils } from 'gipsy-misc'
import { Snackbar } from 'gipsy-ui'

import { highlightElements } from 'features/app/store/actions'
import { updateTaskPartiallyById } from 'features/task/store/actions'

import logger from 'utils/logger'
import { moveElementInItemsfromPositionToPosition } from 'features/todolist/utils'
import { findElementInItemsByPosition } from 'features/list/utils'

export const setTodoListLoaded = createAction(types.SET_TODOLIST_LOADED)
export const setTodoItems = createAction(types.SET_TODO_ITEMS)
export const setTodoItemsForGroup = createAction(types.SET_TODO_ITEMS_FOR_GROUP)
export const addArchiveItems = createAction(types.ADD_ARCHIVE_ITEMS)

/* fetch todolist without any delay */
export const getTodoListWithoutDelay = () => async (dispatch, getState, { api }) => {
  try {
    const data = (await api.todolist.get()) || []
    dispatch(handleTodoListData(data))
    dispatch(setTodoListLoaded(true))
  } catch (err) {
    logger.error(err)
  }
}

/* fetch todolist with 1sec delay and send the request only once, each call of this function will reset the 1s delay */
export const getTodoList = () => async (dispatch, getState, { api }) => {
  try {
    api.todolist.cancel('get')
    const data = (await utils.resolve.awaitOnce('todolist', api.todolist.get)) || []
    dispatch(handleTodoListData(data))
    dispatch(setTodoListLoaded(true))
  } catch (err) {
    if (!err.cancel) {
      logger.error(err)
    }
  }
}

export const cancelGetTodolist = () => async (dispatch, getState, { api }) => {
  utils.resolve.cancel('todolist')
  api.todolist.cancel('get')
}

export const handleTodoListData = data => async (dispatch, getState, { api }) => {
  dispatch(setTodoItems(utils.datetime.groupItemsByDate(data, 'when.date')))
}

/* fetch archivelist without any delay */
export const getArchiveListWithoutDelay = (
  lastSortKey = '',
  limit = constants.limitPaginationArchive,
  rangeQuery = 'lessThan'
) => async (dispatch, getState, { api }) => {
  try {
    const data = (await api.todolist.getArchive(lastSortKey, limit, rangeQuery)) || []
    dispatch(handleArchiveListData(data, lastSortKey, rangeQuery))
  } catch (err) {
    logger.error(err)
  }
}

/* fetch archive list with 1sec delay and send the request only once, each call of this function will reset the 1s delay */
export const getArchiveList = (
  lastSortKey = '',
  limit = constants.limitPaginationArchive,
  rangeQuery = 'lessThan'
) => async (dispatch, getState, { api }) => {
  try {
    api.todolist.cancel('getArchive')
    const data =
      (await utils.resolve.awaitOnce(
        `archive-${lastSortKey}`,
        api.todolist.getArchive.bind(null, lastSortKey, limit, rangeQuery)
      )) || []
    dispatch(handleArchiveListData(data, lastSortKey, rangeQuery))
  } catch (err) {
    if (!err.cancel) {
      logger.error(err)
    }
  }
}

export const handleArchiveListData = (data, lastSortKey, rangeQuery) => async (dispatch, getState, { api }) => {
  try {
    if (lastSortKey && rangeQuery === 'lessThan') {
      return dispatch(addArchiveItems(data.withoutUpdateTasks || []))
    }
    const archive = {
      archiveWithUpdates: {
        list: data.withUpdateTasks || []
      },
      archiveWithoutUpdates: {
        list: data.withoutUpdateTasks || []
      }
    }
    dispatch(setTodoItems({ ...archive }))
  } catch (err) {
    logger.error(err)
  }
}

export const rescheduleOverload = () => async (dispatch, getState, { api }) => {
  try {
    const date = moment().format('YYYY-MM-DD')
    const data = await api.todolist.rescheduleOverload(date)
    if (!data) {
      return
    }
    dispatch(setTodoItems(utils.datetime.groupItemsByDate(data.items, 'when.date')))
    dispatch(highlightElements(data.movedItemsIds))
    setTimeout(() => {
      dispatch(highlightElements())
    }, 10 * 1000)
    const defaultCode = 'rescheduleOverload.ok'
    dispatch(
      Snackbar.show(
        {
          message: get(translations, `message.success.${data.code || defaultCode}`),
          status: data.code ? 'error' : 'success'
        },
        15000
      )
    )
  } catch (err) {
    dispatch(handleAPIError(err))
  }
}

/* from / to should be an object referencing the table and index in this table { table : 0, index: 0 } */
export const dragAndDrop = ({ id, from, to }) => (dispatch, getState, { api }) => {
  dispatch(cancelGetTodolist())
  return new Promise((resolve, reject) => {
    /* prevent to show the invalid date label when dropped into upcoming */
    if (from.table !== to.table && utils.datetime.isGroupUpcoming(to.table)) {
      dispatch(updateTaskPartiallyById(id, { when: { date: null } }))
    }
    const todoItems = getState().todolist.items
    const todoItemToMove = findElementInItemsByPosition(todoItems, {
      group: from.table,
      index: from.index
    })
    let newTodoItems = moveElementInItemsfromPositionToPosition(
      todoItemToMove,
      todoItems,
      { group: from.table, index: from.index },
      { group: to.table, index: to.index }
    )
    dispatch(setTodoItems(newTodoItems))

    const dragAndDropUpdate = utils.list.buildDragAndDropUpdateBody(to.table, to.index, newTodoItems[to.table].list)
    if (!dragAndDropUpdate) {
      dispatch(
        handleAPIError("Couldn't build dragAndDropUpdate body for request", {
          todoItems,
          from,
          to
        })
      )
      return
    }
    api.task
      .dragAndDrop(dragAndDropUpdate)
      .then(response => {
        dispatch(getTodoList())
        resolve(response)
      })
      .catch(err => {
        dispatch(handleAPIError(err, { dragAndDropUpdate, from, to }))
        reject(err)
      })
  })
}



// WEBPACK FOOTER //
// ./src/features/todolist/store/actions.js